import MoreHorizIcon from "@mui/icons-material/MoreHoriz"
import {
    Box,
    Checkbox,
    Collapse,
    FormControlLabel,
    Slider,
    Stack,
    Typography,
} from "@mui/material"
import Grid from "@mui/material/Unstable_Grid2"
import { ReactElement, SyntheticEvent, useState } from "react"
import { useSearchParams } from "react-router-dom"

import AppLoadingButton from "../../components/buttons/appLoadingButton"
import DataLoadError from "../../components/fallback/dataLoadError"
import ApplicationsFilterPanelFrame from "../../components/fitlerPanel/applicationsFilterPanelFrame"
import AppControlledDateRange from "../../components/inputs/controlled/appControlledDateRange"
import AppControlledSelect from "../../components/inputs/controlled/appControlledSelect"
import DebouncedTextField from "../../components/inputs/debouncedTextField"
import MonthBucketSkeleton from "../../components/loading/skeletons/monthBucketSkeleton"
import { LCT_TILES } from "../../components/submitApplication/single/selectLct/applicationTypeTiles"
import AppPaper from "../../components/surfaces/appPaper"
import MonthHeader from "../../components/surfaces/monthHeader"
import InstallerApplicationTable from "../../components/tables/applicationTable/installerApplicationTable"
import {
    ApplicationDisplayStatuses,
    InstallerApplicationStatuses,
} from "../../constants/enums"
import {
    ADDRESS,
    APPLICATION_ID_KW,
    INSTALLER_EMAIL,
    POSTCODE,
    READY_FOR_CX,
    SLIDE_IN_TIMEOUT,
    STATUS,
    TECHNOLOGY,
    MIN_GEN_CAPACITY,
    MAX_GEN_CAPACITY,
    MIN_MAX_DEMAND,
    MAX_MAX_DEMAND,
    INSTALLER_DATE_TO_KW,
    INSTALLER_DATE_FROM_KW,
    UPDATED_SINCE,
    UPDATED_TO,
} from "../../constants/generalConstants"
import {
    TT_HELP_INSTALLER_ADMIN_LIST_APPLICATIONS,
    TT_HELP_INSTALLER_LIST_APPLICATIONS,
} from "../../constants/tooltips"
import { useApplicationDeriveFilters } from "../../hooks/useApplicationsDeriveFiltersFromUrl"
import { useGetState } from "../../hooks/useGetState"
import { useGetUserData } from "../../hooks/useGetUserData"
import { getShowDnoApplicationFilters } from "../../redux/preferencesSlice"
import { useGetInstallerApplicationsMonthly } from "../../services/middleware/hooks/installerInterface/useGetInstallerApplicationsMonthly"
import { GRID_SPACING } from "../../themes/defaultThemeParams"

const InstallerListApplications = (): ReactElement => {
    const [searchParams, setSearchParams] = useSearchParams()
    const {
        allData,
        isError,
        isLoadingAny,
        isAutoFilling,
        monthBuckets,
        isFinalMonth,
        loadMore,
    } = useGetInstallerApplicationsMonthly(searchParams, true)

    const {
        applicationId,
        setFilterFromTextfield,
        orderBy,
        orderDirection,
        dateFrom,
        dateTo,
        updatedSince,
        updatedTo,
        lastUpdated,
        status,
        technology,
        postcode,
        setOrderBy,
        address,
        installerEmail,
        genCapacityMin,
        genCapacityMax,
        totDemCapacityMin,
        totDemCapacityMax,
        setFilterFromSlider,
        readyForCx,
        isLoopedSupply,
        isSupplyIssues,
        isUnread,
        isRedactedOnly,
    } = useApplicationDeriveFilters(searchParams, setSearchParams, true)
    // Local states only to be used sparingly - where possible rely on URL
    // params - this is to reduce renders
    const [localApplicationId, setLocalApplicationId] =
        useState<string>(applicationId)
    const [localPostcode, setLocalPostcode] = useState<string>(postcode || "")
    const [localAddress, setLocalAddress] = useState<string>(address || "")
    const [localInstallerEmail, setLocalInstallerEmail] = useState<string>(
        installerEmail || ""
    )
    const MAX_CAPACITY = 100
    const TOT_DEM_MAX_CAPACITY = 100
    const handleSortChange = (newSortKey: string): void => {
        // Monthly hook sorts by updatedSince inherently; no-op
    }
    const [localSliderValue, setLocalSliderValue] = useState<[number, number]>([
        genCapacityMin,
        genCapacityMax,
    ])
    const [localTotDemSliderValue, setLocalTotDemSliderValue] = useState<
        [number, number]
    >([totDemCapacityMin, totDemCapacityMax])

    // Handle changes during slider movement (updates local state only)
    const onSlide = (e_: Event, newValue: number | number[]): void => {
        setLocalSliderValue(newValue as [number, number])
    }

    const onTotDemSlide = (e_: Event, newValue: number | number[]): void => {
        setLocalTotDemSliderValue(newValue as [number, number])
    }

    // These functions handle the final value when slider is released
    const onGenCapacitySliderCommitted = (
        e: Event | SyntheticEvent<Element, Event>,
        value: number | number[]
    ): void => {
        setFilterFromSlider(e, value, [MIN_GEN_CAPACITY, MAX_GEN_CAPACITY])
    }

    const onTotDemSliderCommitted = (
        e: Event | SyntheticEvent<Element, Event>,
        value: number | number[]
    ): void => {
        setFilterFromSlider(e, value, [MIN_MAX_DEMAND, MAX_MAX_DEMAND])
    }

    const technologyMapping = LCT_TILES.map((tile) => ({
        label: tile.title,
        value: tile.value,
    }))
    const techOptions: string[] = LCT_TILES.map((tile) => tile.title)

    const showFilters = useGetState(getShowDnoApplicationFilters)
    const { isInstallerAdmin } = useGetUserData()
    return (
        <Grid container spacing={GRID_SPACING}>
            <ApplicationsFilterPanelFrame
                applicationId={applicationId}
                isValidating={isLoadingAny}
                all={!!isInstallerAdmin}
                applications={
                    isInstallerAdmin
                        ? "Applications Admin View"
                        : "My Applications"
                }
                tooltip={
                    isInstallerAdmin
                        ? TT_HELP_INSTALLER_ADMIN_LIST_APPLICATIONS
                        : TT_HELP_INSTALLER_LIST_APPLICATIONS
                }
                isDnoMode={false}
                clearLocalAddress={() => {
                    setLocalAddress("")
                    setFilterFromTextfield("", ADDRESS)
                }}
                clearLocalPostcode={() => {
                    setLocalPostcode("")
                    setFilterFromTextfield("", POSTCODE)
                }}
                clearLocalInstallerEmail={() => {
                    setLocalInstallerEmail("")
                    setFilterFromTextfield("", INSTALLER_EMAIL)
                }}
                clearLocalGenCapacity={() => {
                    setLocalSliderValue([0, MAX_CAPACITY])
                    setFilterFromSlider(
                        {} as SyntheticEvent<Element, Event>,
                        [0, MAX_CAPACITY],
                        [MIN_GEN_CAPACITY, MAX_GEN_CAPACITY]
                    )
                }}
                clearLocalDemCapacity={() => {
                    setLocalTotDemSliderValue([0, TOT_DEM_MAX_CAPACITY])
                    setFilterFromSlider(
                        {} as SyntheticEvent<Element, Event>,
                        [0, MAX_CAPACITY],
                        [MIN_MAX_DEMAND, MAX_MAX_DEMAND]
                    )
                }}
            />
            <Grid xs={12}>
                <AppPaper>
                    <Collapse in={showFilters} timeout={SLIDE_IN_TIMEOUT}>
                        <Grid
                            container
                            spacing={GRID_SPACING + 1}
                            marginBottom={4}
                            marginTop={1}
                            columns={12}
                            display="flex"
                            alignItems="center"
                        >
                            <Grid xl={3} md={6} sm={12} xs={12}>
                                <DebouncedTextField
                                    label="Application ID"
                                    value={localApplicationId}
                                    placeholder="Full application ID required..."
                                    onDebouncedChange={(value) => {
                                        setLocalApplicationId(value)
                                        setFilterFromTextfield(
                                            value,
                                            APPLICATION_ID_KW
                                        )
                                    }}
                                />
                            </Grid>
                            <Grid xl={3} md={6} sm={12} xs={12}>
                                <AppControlledDateRange
                                    label="Date from"
                                    dateValue={dateFrom}
                                    setDateValue={(v: string) =>
                                        setFilterFromTextfield(
                                            v,
                                            INSTALLER_DATE_FROM_KW
                                        )
                                    }
                                />
                            </Grid>
                            <Grid xl={3} md={6} sm={12} xs={12}>
                                <AppControlledDateRange
                                    label="Date to"
                                    dateValue={dateTo}
                                    setDateValue={(v: string) =>
                                        setFilterFromTextfield(
                                            v,
                                            INSTALLER_DATE_TO_KW
                                        )
                                    }
                                />
                            </Grid>
                            <Grid xl={3} md={6} sm={12} xs={12}>
                                <AppControlledDateRange
                                    label="Last Updated since"
                                    dateValue={updatedSince}
                                    setDateValue={(v: string) =>
                                        setFilterFromTextfield(v, UPDATED_SINCE)
                                    }
                                />
                            </Grid>
                            <Grid xl={3} md={6} sm={12} xs={12}>
                                <AppControlledDateRange
                                    label="Last Updated to"
                                    dateValue={updatedTo}
                                    setDateValue={(v: string) =>
                                        setFilterFromTextfield(v, UPDATED_TO)
                                    }
                                />
                            </Grid>
                            <Grid xl={3} md={6} sm={12} xs={12}>
                                <AppControlledSelect
                                    value={status}
                                    options={Object.values(
                                        InstallerApplicationStatuses
                                    )}
                                    label="Status"
                                    setValue={(
                                        e_: SyntheticEvent<Element, Event>,
                                        nv: string | number | null
                                    ) =>
                                        setFilterFromTextfield(
                                            nv as string,
                                            STATUS
                                        )
                                    }
                                    getOptionLabel={(option) =>
                                        String(option) ===
                                        InstallerApplicationStatuses.dnoFurtherInfoRequired
                                            ? ApplicationDisplayStatuses.installerInformationRequired
                                            : String(option)
                                    }
                                    loading={false}
                                    fullWidth
                                />
                            </Grid>
                            <Grid xl={3} md={6} sm={12} xs={12}>
                                <AppControlledSelect
                                    value={technology}
                                    options={techOptions}
                                    label="Technology"
                                    setValue={(
                                        _event: SyntheticEvent<Element, Event>,
                                        newValue: string | number | null
                                    ) => {
                                        const selectedTile =
                                            technologyMapping.find(
                                                (item) =>
                                                    item.label === newValue
                                            )
                                        setFilterFromTextfield(
                                            selectedTile?.value || "",
                                            TECHNOLOGY
                                        )
                                    }}
                                    loading={false}
                                    fullWidth
                                />
                            </Grid>
                            <Grid xl={3} md={6} sm={12} xs={12}>
                                <DebouncedTextField
                                    label="Installer Email"
                                    value={localInstallerEmail}
                                    placeholder="Enter Installers email..."
                                    onDebouncedChange={(value) => {
                                        setLocalInstallerEmail(value)
                                        setFilterFromTextfield(
                                            value,
                                            INSTALLER_EMAIL
                                        )
                                    }}
                                />
                            </Grid>
                            <Grid xl={3} md={6} sm={12} xs={12}>
                                <DebouncedTextField
                                    label="Postcode"
                                    value={localPostcode}
                                    placeholder="Enter postcode..."
                                    onDebouncedChange={(value) => {
                                        setLocalPostcode(value.toUpperCase())
                                        setFilterFromTextfield(
                                            value.toUpperCase(),
                                            POSTCODE
                                        )
                                    }}
                                />
                            </Grid>
                            <Grid xl={3} md={6} sm={12} xs={12}>
                                <DebouncedTextField
                                    label="Address"
                                    value={localAddress}
                                    placeholder="Enter address..."
                                    onDebouncedChange={(value) => {
                                        setLocalAddress(value)
                                        setFilterFromTextfield(value, ADDRESS)
                                    }}
                                />
                            </Grid>
                            <Grid xl={3} md={6} sm={12} xs={12}>
                                <Typography variant="body1" gutterBottom>
                                    {`Generation Capacity
                        : ${localSliderValue[0]} - ${localSliderValue[1]}${
                            localSliderValue[1] === MAX_CAPACITY ? "+" : ""
                        } kW`}
                                </Typography>
                                <Box
                                    sx={{ width: "90%", justifySelf: "center" }}
                                >
                                    <Slider
                                        value={localSliderValue}
                                        max={MAX_CAPACITY}
                                        onChange={onSlide}
                                        onChangeCommitted={
                                            onGenCapacitySliderCommitted
                                        }
                                        valueLabelDisplay="auto"
                                        getAriaLabel={(index) =>
                                            `capacitySlider${index}`
                                        }
                                        marks={[
                                            { value: 0, label: "0" },
                                            {
                                                value: MAX_CAPACITY,
                                                label: `${MAX_CAPACITY}`,
                                            },
                                        ]}
                                    />
                                </Box>
                            </Grid>
                            <Grid xl={3} md={6} sm={12} xs={12}>
                                <Typography variant="body1" gutterBottom>
                                    {`Total demand
                        : ${localTotDemSliderValue[0]} - ${
                            localTotDemSliderValue[1]
                        }${
                            localTotDemSliderValue[1] === TOT_DEM_MAX_CAPACITY
                                ? "+"
                                : ""
                        } A`}
                                </Typography>
                                <Box
                                    sx={{ width: "90%", justifySelf: "center" }}
                                >
                                    <Slider
                                        value={localTotDemSliderValue}
                                        max={TOT_DEM_MAX_CAPACITY}
                                        onChange={onTotDemSlide}
                                        onChangeCommitted={
                                            onTotDemSliderCommitted
                                        }
                                        valueLabelDisplay="auto"
                                        getAriaLabel={(index) =>
                                            `capacitySlider${index}`
                                        }
                                        marks={[
                                            { value: 0, label: "0" },
                                            {
                                                value: TOT_DEM_MAX_CAPACITY,
                                                label: `${TOT_DEM_MAX_CAPACITY}`,
                                            },
                                        ]}
                                    />
                                </Box>
                            </Grid>
                            <Grid xl={3} md={6} sm={12} xs={12}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={readyForCx}
                                            onChange={(e) => {
                                                setFilterFromTextfield(
                                                    String(e.target.checked),
                                                    READY_FOR_CX
                                                )
                                            }}
                                        />
                                    }
                                    label="Ready for Commissioning Only"
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={isLoopedSupply}
                                            onChange={(e) => {
                                                setFilterFromTextfield(
                                                    String(e.target.checked),
                                                    "isLoopedSupply"
                                                )
                                            }}
                                        />
                                    }
                                    label="Looped Supply?"
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={isSupplyIssues}
                                            onChange={(e) => {
                                                setFilterFromTextfield(
                                                    String(e.target.checked),
                                                    "isSupplyIssues"
                                                )
                                            }}
                                        />
                                    }
                                    label="Any Supply Issues?"
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={isUnread}
                                            onChange={(e) => {
                                                setFilterFromTextfield(
                                                    String(e.target.checked),
                                                    "isUnread"
                                                )
                                            }}
                                        />
                                    }
                                    label="Any Unread Comments?"
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={isRedactedOnly}
                                            onChange={(e) => {
                                                setFilterFromTextfield(
                                                    String(e.target.checked),
                                                    "redactedOnly"
                                                )
                                            }}
                                        />
                                    }
                                    label="Redacted applications over 6 months"
                                />
                            </Grid>
                        </Grid>
                    </Collapse>
                    {isError ? (
                        <DataLoadError />
                    ) : (
                        <Stack spacing={GRID_SPACING}>
                            {monthBuckets.map((bucket) => (
                                <Collapse
                                    key={bucket.key}
                                    in
                                    appear
                                    timeout={300}
                                >
                                    <Box>
                                        <MonthHeader
                                            label={bucket.label}
                                            recordCount={bucket.data.length}
                                            isLoading={bucket.isLoading}
                                            monthKey={bucket.key}
                                        />
                                        <InstallerApplicationTable
                                            searchResults={bucket.data}
                                            orderBy={orderBy}
                                            setOrderBy={setOrderBy}
                                            orderDirection={orderDirection}
                                            isLoading={
                                                bucket.data.length === 0 &&
                                                !bucket.isComplete
                                            }
                                            onSortChange={handleSortChange}
                                        />
                                    </Box>
                                </Collapse>
                            ))}
                            {(isAutoFilling || isLoadingAny) && !isError && (
                                <MonthBucketSkeleton />
                            )}
                            {allData && (
                                <Stack
                                    alignItems="center"
                                    justifyContent="center"
                                    direction="row"
                                    width="100%"
                                >
                                    {!isFinalMonth && (
                                        <AppLoadingButton
                                            color="secondary"
                                            endIcon={<MoreHorizIcon />}
                                            loading={isLoadingAny}
                                            loadingPosition="end"
                                            onClick={loadMore}
                                            sx={{
                                                width: "40%",
                                                mt: "6px",
                                            }}
                                        >
                                            See More
                                        </AppLoadingButton>
                                    )}
                                </Stack>
                            )}
                        </Stack>
                    )}
                </AppPaper>
            </Grid>
        </Grid>
    )
}

export default InstallerListApplications
